// M017 (Captain pivot): reusable typeable-autocomplete for model
// selection on Personas cards + create form. Same keyboard-nav
// pattern as the @ mention menu in the chat composer — type to
// filter, ↑/↓ to navigate, Enter/Tab to select, Esc to close.

import { useEffect, useMemo, useRef, useState } from "react";
import type { OpenRouterModel } from "../../api/openrouter";

interface Props {
  value: string;
  models: OpenRouterModel[];
  onChange: (next: string) => void;
  onCommit?: (next: string) => void;
  placeholder?: string;
  disabled?: boolean;
  testId?: string;
  autoFocus?: boolean;
}

export function ModelAutocomplete({
  value,
  models,
  onChange,
  onCommit,
  placeholder = "pick a model…",
  disabled = false,
  testId,
  autoFocus = false,
}: Props) {
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return models.slice(0, 24);
    return models
      .filter((m) => m.id.toLowerCase().includes(q))
      .slice(0, 24);
  }, [query, models]);

  useEffect(() => {
    if (activeIdx >= matches.length) setActiveIdx(0);
  }, [matches, activeIdx]);

  function commit(next: string) {
    setQuery(next);
    setOpen(false);
    onChange(next);
    onCommit?.(next);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
      e.preventDefault();
      return;
    }
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") {
      setActiveIdx((i) => Math.min(i + 1, matches.length - 1));
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setActiveIdx((i) => Math.max(i - 1, 0));
      e.preventDefault();
    } else if (e.key === "Enter" || e.key === "Tab") {
      if (matches[activeIdx]) {
        commit(matches[activeIdx].id);
        e.preventDefault();
      }
    }
  }

  return (
    <div className="relative" ref={wrapperRef}>
      <input
        ref={inputRef}
        type="text"
        value={query}
        placeholder={placeholder}
        onChange={(e) => {
          setQuery(e.target.value);
          onChange(e.target.value);
          setOpen(true);
          setActiveIdx(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        disabled={disabled}
        autoFocus={autoFocus}
        autoComplete="off"
        spellCheck={false}
        className="w-full rounded-md border border-edge bg-surface-raised px-2 py-1 font-code text-xs text-primary outline-none focus:border-border-highlight disabled:cursor-not-allowed disabled:opacity-60"
        data-testid={testId}
      />
      {open && matches.length > 0 && (
        <ul
          className="absolute left-0 right-0 z-50 mt-1 max-h-60 overflow-y-auto rounded-md border border-edge bg-surface-raised shadow-[var(--wt-shadow)]"
          role="listbox"
        >
          {matches.map((m, i) => {
            const isActive = i === activeIdx;
            return (
              <li key={m.id}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    commit(m.id);
                  }}
                  onMouseEnter={() => setActiveIdx(i)}
                  className={`flex w-full cursor-pointer items-center justify-between gap-2 px-2 py-1 text-left font-code text-xs transition-colors ${
                    isActive
                      ? "bg-surface text-primary"
                      : "text-muted hover:bg-surface hover:text-primary"
                  }`}
                >
                  <span className="truncate">{m.id}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
